/**
 * Design System Reference Page
 * Quick access to Figma design tokens with live examples
 */

import { PageHeader } from '@/components/common/PageHeader';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { MetricCard, KPIGrid } from '@/components/common/MetricCard';
import { DollarSign, TrendingUp } from 'lucide-react';
import { DataTable } from '@/components/common/data-table/DataTable';
import { type ColumnDef } from '@tanstack/react-table';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

// Sample data for DataTable examples
interface SampleProduct {
  id: string;
  name: string;
  sku: string;
  category: string;
  quantity: number;
  status: 'In Stock' | 'Low Stock' | 'Out of Stock';
}

const sampleData: SampleProduct[] = [
  {
    id: '1',
    name: '4" Gate Valve',
    sku: 'VLV-001',
    category: 'Valves',
    quantity: 150,
    status: 'In Stock',
  },
  {
    id: '2',
    name: 'Drill Pipe 5"',
    sku: 'DRL-045',
    category: 'Drilling',
    quantity: 25,
    status: 'Low Stock',
  },
  {
    id: '3',
    name: 'BOP Control Pod',
    sku: 'BOP-201',
    category: 'BOP Equipment',
    quantity: 0,
    status: 'Out of Stock',
  },
  {
    id: '4',
    name: 'Mud Pump Liner',
    sku: 'MUD-112',
    category: 'Pumps',
    quantity: 75,
    status: 'In Stock',
  },
];

const sampleColumns: ColumnDef<SampleProduct>[] = [
  { id: 'name', accessorKey: 'name', header: 'Product Name' },
  { id: 'sku', accessorKey: 'sku', header: 'SKU' },
  { id: 'category', accessorKey: 'category', header: 'Category' },
  { id: 'quantity', accessorKey: 'quantity', header: 'Quantity' },
  {
    id: 'status',
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => {
      const status = row.original.status;
      const variant =
        status === 'In Stock' ? 'default' : status === 'Low Stock' ? 'secondary' : 'destructive';
      return <Badge variant={variant as any}>{status}</Badge>;
    },
  },
];

export function DesignSystem() {
  return (
    <div className="space-y-6">
      <PageHeader
        title="Figma Design System"
        description="Design tokens, components, and usage examples for the Field Pulse interface."
      />

      {/* Typography Examples */}
      <Card>
        <CardHeader>
          <CardTitle>Typography Scale</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            One size and weight per role. Headings are set by element level, so pick the level that
            matches the document structure rather than the size you want.
          </p>
          <div className="space-y-3">
            <div>
              <code className="text-xs text-muted-foreground">
                h1 · text-2xl font-bold — page title
              </code>
              <h1 className="text-2xl font-bold tracking-tight">Page Title (24px / 700)</h1>
            </div>
            <div>
              <code className="text-xs text-muted-foreground">
                h2 · text-lg font-semibold — section
              </code>
              <h2 className="text-lg font-semibold">Section Heading (18px / 600)</h2>
            </div>
            <div>
              <code className="text-xs text-muted-foreground">
                h3 · text-base font-semibold — subsection
              </code>
              <h3 className="text-base font-semibold">Subsection Heading (16px / 600)</h3>
            </div>
            <div>
              <code className="text-xs text-muted-foreground">
                h4 · text-sm font-semibold — minor
              </code>
              <h4 className="text-sm font-semibold">Minor Heading (14px / 600)</h4>
            </div>
            <div>
              <code className="text-xs text-muted-foreground">text-sm — body (the base size)</code>
              <p className="text-sm">
                Body (14px / 400) — Lorem ipsum dolor sit amet, consectetur adipiscing elit.
              </p>
            </div>
            <div>
              <code className="text-xs text-muted-foreground">
                text-xs text-muted-foreground — caption
              </code>
              <p className="text-xs text-muted-foreground">Caption (12px / 400) — helper text</p>
            </div>
            <div>
              <code className="text-xs text-muted-foreground">
                text-[11px] font-semibold uppercase tracking-wider — eyebrow
              </code>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Section Label (11px / 600)
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Shadow Examples */}
      <Card>
        <CardHeader>
          <CardTitle>Elevation / Shadows</CardTitle>
        </CardHeader>
        <CardContent>
          {/* Roles, not sizes: pick by what the thing is. */}
          <div className="grid grid-cols-2 gap-6 lg:grid-cols-4">
            {[
              ['shadow-raised', 'Panel at rest'],
              ['shadow-raised-hover', 'Panel under the pointer'],
              ['shadow-overlay', 'Menu on its trigger'],
              ['shadow-floating', 'Dialog, toast, action bar'],
            ].map(([token, role]) => (
              <div key={token}>
                <p className="mb-1 text-sm font-medium text-foreground">{token}</p>
                <p className="mb-3 text-xs text-muted-foreground">{role}</p>
                <div
                  className={cn(
                    'flex h-24 items-center justify-center rounded-lg bg-card text-sm',
                    token
                  )}
                >
                  {role}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Font Families */}
      <Card>
        <CardHeader>
          <CardTitle>Font Families</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <code className="text-xs text-muted-foreground">font-sans (Geist)</code>
            <p className="font-sans text-lg">The quick brown fox jumps over the lazy dog</p>
          </div>
          <div>
            <code className="text-xs text-muted-foreground">font-mono (JetBrains Mono)</code>
            <p className="font-mono text-lg">const example = "Hello World";</p>
          </div>
        </CardContent>
      </Card>

      {/* Tailwind Integration */}
      <Card>
        <CardHeader>
          <CardTitle>Tailwind Classes</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <code className="text-xs text-muted-foreground mb-2 block">
              className="text-2xl font-bold"
            </code>
            <h1 className="text-2xl font-bold tracking-tight">Page title via Tailwind</h1>
          </div>
          <div>
            <code className="text-xs text-muted-foreground mb-2 block">className="text-sm"</code>
            <p className="text-sm">Body text at the base size via a stock Tailwind utility</p>
          </div>
          <div>
            <code className="text-xs text-muted-foreground mb-2 block">
              className="shadow-overlay rounded-lg p-6"
            </code>
            <div className="shadow-overlay rounded-lg p-6 bg-white">
              Card with Figma overlay shadow
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Component Integration Examples */}
      <Card>
        <CardHeader>
          <CardTitle>Component Integration</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <p className="text-sm text-muted-foreground mb-4">
              Components take their typography from the shared scale automatically — there is no
              per-component opt-in
            </p>

            <div className="space-y-6">
              {/* Default Typography */}
              <div>
                <p className="text-xs font-semibold uppercase text-muted-foreground mb-3 tracking-wider">
                  Default Typography (CSS Classes)
                </p>
                <KPIGrid columns={2}>
                  <MetricCard
                    label="Total Revenue"
                    value={125400}
                    sentiment="success"
                    prefix="$"
                    icon={<DollarSign className="h-5 w-5" />}
                    subtitle="Last 30 days"
                  />
                  <MetricCard
                    label="Growth Rate"
                    value={23.5}
                    sentiment="success"
                    suffix="%"
                    icon={<TrendingUp className="h-5 w-5" />}
                    trend={{
                      direction: 'up',
                      value: '+5.2%',
                      text: 'vs previous',
                    }}
                  />
                </KPIGrid>
              </div>

              {/* Figma Typography */}
              <div>
                <p className="text-xs font-semibold uppercase text-muted-foreground mb-3 tracking-wider">
                  Figma Design System Typography
                </p>
                <KPIGrid columns={2}>
                  <MetricCard
                    label="Total Revenue"
                    value={125400}
                    sentiment="success"
                    prefix="$"
                    icon={<DollarSign className="h-5 w-5" />}
                    subtitle="Last 30 days"
                  />
                  <MetricCard
                    label="Growth Rate"
                    value={23.5}
                    sentiment="success"
                    suffix="%"
                    icon={<TrendingUp className="h-5 w-5" />}
                    trend={{
                      direction: 'up',
                      value: '+5.2%',
                      text: 'vs previous',
                    }}
                  />
                </KPIGrid>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* DataTable Integration */}
      <Card>
        <CardHeader>
          <CardTitle>DataTable Typography</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <p className="text-sm text-muted-foreground mb-4">
              Table headers use the eyebrow treatment (11px / 600 / uppercase) and cells the body
              size (14px)
            </p>

            {/* Default Typography Table */}
            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase text-muted-foreground tracking-wider">
                Default Typography (CSS Classes)
              </p>
              <DataTable columns={sampleColumns} data={sampleData} pagination={false} />
            </div>

            {/* Figma Typography Table */}
            <div className="space-y-3 mt-6">
              <p className="text-xs font-semibold uppercase text-muted-foreground tracking-wider">
                Figma Design System Typography
              </p>
              <DataTable columns={sampleColumns} data={sampleData} pagination={false} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Card Typography */}
      <Card>
        <CardHeader>
          <CardTitle>Card Typography</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <p className="text-sm text-muted-foreground mb-4">
              Card titles sit at the h3 tier (16px / 600) and descriptions at body size (14px)
            </p>

            {/* Default Typography Cards */}
            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase text-muted-foreground tracking-wider">
                Default Typography (CSS Classes)
              </p>
              <div className="grid grid-cols-2 gap-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Inventory Management</CardTitle>
                    <CardDescription>
                      Track and manage inventory levels across all locations
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      Monitor stock levels, set reorder points, and receive alerts.
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle>Work Orders</CardTitle>
                    <CardDescription>
                      Schedule and track maintenance work across equipment
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      Create, assign, and complete work orders efficiently.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Figma Typography Cards */}
            <div className="space-y-3 mt-6">
              <p className="text-xs font-semibold uppercase text-muted-foreground tracking-wider">
                Figma Design System Typography
              </p>
              <div className="grid grid-cols-2 gap-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Inventory Management</CardTitle>
                    <CardDescription>
                      Track and manage inventory levels across all locations
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      Monitor stock levels, set reorder points, and receive alerts.
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle>Work Orders</CardTitle>
                    <CardDescription>
                      Schedule and track maintenance work across equipment
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      Create, assign, and complete work orders efficiently.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Badge, Dialog, Label Typography */}
      <Card>
        <CardHeader>
          <CardTitle>Additional Components</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Badge */}
          <div>
            <p className="text-xs font-semibold uppercase text-muted-foreground tracking-wider mb-3">
              Badge Typography
            </p>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <p className="text-xs text-muted-foreground mb-2">Default</p>
                <div className="flex flex-wrap gap-2">
                  <Badge>In Stock</Badge>
                  <Badge variant="secondary">Low Stock</Badge>
                  <Badge variant="destructive">Out of Stock</Badge>
                  <Badge variant="outline">Pending</Badge>
                </div>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-2">Figma Typography</p>
                <div className="flex flex-wrap gap-2">
                  <Badge>In Stock</Badge>
                  <Badge variant="secondary">Low Stock</Badge>
                  <Badge variant="destructive">Out of Stock</Badge>
                  <Badge variant="outline">Pending</Badge>
                </div>
              </div>
            </div>
          </div>

          {/* Label (Form) */}
          <div>
            <p className="text-xs font-semibold uppercase text-muted-foreground tracking-wider mb-3">
              Label Typography (Forms)
            </p>
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-4">
                <p className="text-xs text-muted-foreground mb-2">Default</p>
                <div className="space-y-2">
                  <Label htmlFor="email1">Email Address</Label>
                  <Input id="email1" type="email" placeholder="Enter your email" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password1">Password</Label>
                  <Input id="password1" type="password" placeholder="Enter your password" />
                </div>
              </div>
              <div className="space-y-4">
                <p className="text-xs text-muted-foreground mb-2">Figma Typography</p>
                <div className="space-y-2">
                  <Label htmlFor="email2">Email Address</Label>
                  <Input id="email2" type="email" placeholder="Enter your email" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password2">Password</Label>
                  <Input id="password2" type="password" placeholder="Enter your password" />
                </div>
              </div>
            </div>
          </div>

          {/* Dialog/Modal */}
          <div>
            <p className="text-xs font-semibold uppercase text-muted-foreground tracking-wider mb-3">
              Dialog Typography
            </p>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <p className="text-xs text-muted-foreground mb-2">Default</p>
                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="outline">Open Dialog (Default)</Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Confirm Action</DialogTitle>
                      <DialogDescription>
                        Are you sure you want to proceed with this action? This cannot be undone.
                      </DialogDescription>
                    </DialogHeader>
                  </DialogContent>
                </Dialog>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-2">Figma Typography</p>
                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="outline">Open Dialog (Figma)</Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Confirm Action</DialogTitle>
                      <DialogDescription>
                        Are you sure you want to proceed with this action? This cannot be undone.
                      </DialogDescription>
                    </DialogHeader>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Code Example */}
      <Card>
        <CardHeader>
          <CardTitle>Usage Example</CardTitle>
        </CardHeader>
        <CardContent>
          <pre className="bg-muted text-muted-foreground p-4 rounded-lg overflow-x-auto text-sm">
            <code>{`// Import design tokens
import { designTokens } from '@/lib/figma-design-tokens';

// Headings: pick the level, the scale supplies size and weight
<h1 className="text-2xl font-bold tracking-tight">
  Dashboard
</h1>

// Body copy sits at the 14px base size
<p className="text-sm">Overview of today's operations</p>

// Direct token access
<div style={{
  boxShadow: designTokens.shadows.overlay,
  borderRadius: designTokens.borderRadius.lg,
}}>
  Custom styling
</div>

// Option 4: PageHeader with Figma typography
<PageHeader
  title="Inventory"
  description="Manage your inventory items"
/>

// Option 5: MetricCard with Figma typography
<MetricCard
  label="Total Revenue"
  value={125400}
  sentiment="success"
  prefix="$"
  icon={<DollarSign />}
/>

// Option 6: DataTable with Figma typography
<DataTable
  columns={columns}
  data={data}
  pagination
  searchable
/>

// Option 7: Card with Figma typography
<Card>
  <CardHeader>
    <CardTitle>Dashboard</CardTitle>
    <CardDescription>
      View key metrics and insights
    </CardDescription>
  </CardHeader>
  <CardContent>
    {/* Card content */}
  </CardContent>
</Card>

// Option 8: Badge with Figma typography
<Badge variant="default">
  Active
</Badge>

// Option 9: Dialog with Figma typography
<Dialog>
  <DialogTrigger asChild>
    <Button>Open Dialog</Button>
  </DialogTrigger>
  <DialogContent>
    <DialogHeader>
      <DialogTitle>Confirmation</DialogTitle>
      <DialogDescription>
        Please confirm this action
      </DialogDescription>
    </DialogHeader>
  </DialogContent>
</Dialog>

// Option 10: Label with Figma typography
<div className="space-y-2">
  <Label htmlFor="email">
    Email Address
  </Label>
  <Input id="email" type="email" />
</div>`}</code>
          </pre>
        </CardContent>
      </Card>

      {/* Documentation Link */}
      <Card className="bg-primary/5 border-primary/20">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="rounded-lg bg-primary/10 p-3">
              <svg
                className="h-6 w-6 text-primary"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-base font-semibold mb-1">Complete Documentation</h3>
              <p className="text-sm text-muted-foreground mb-3">
                View comprehensive documentation, component inventory, and integration guides.
              </p>
              <a
                href="/docs/FIGMA_DESIGN_SYSTEM.md"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-primary hover:underline"
              >
                Read the full documentation →
              </a>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default DesignSystem;
